import { type FC, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { axiosPost } from "@root/shared/utils/axiosForRails";
import { showAlert } from "@root/shared/utils";
import axios from "axios";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema, type FormInputs } from "./schema";
import { type Props, type Response, type Entries } from "./type";
import { Presentation } from "./Presentation";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const newrelic: any;

export const ExtendScreeningDeadlineModal: FC<Props> = ({
  reasons,
  extendJobEntryScreeningDeadlinesUrl,
  redirectUrl,
  isSp,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");

  const methods = useForm<FormInputs>({
    defaultValues: {
      reason: "",
      description: "",
    },
    resolver: zodResolver(schema),
  });

  const handleClick = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);
  const handleRadioChange = (value: string) => {
    methods.clearErrors("reason");
    methods.clearErrors("description");
    setSelectedReason(value);
    // MEMO: iOSの場合にreasonがうまく設定されないのでここで選択した値をセットする
    methods.setValue("reason", value);
  };
  const handleTextAreaChange = () => methods.clearErrors("description");

  const getEntries = <T extends Record<string, unknown>>(obj: T): Entries<T> => {
    return Object.entries(obj) as Entries<T>;
  };

  const onSubmit: SubmitHandler<FormInputs> = async (formData) => {
    const params: FormInputs = {
      description: formData.description,
      reason: formData.reason,
    };
    try {
      await axiosPost.post(extendJobEntryScreeningDeadlinesUrl, params);
      window.location.href = redirectUrl;
    } catch (error) {
      if (!axios.isAxiosError(error) || !error.response) {
        showAlert("送信に失敗しました。時間をおいて再度お試しください。");
        return;
      }

      const { status, data }: { status: number; data: Response } = error.response;

      switch (status) {
        case 400:
          if (data.errors) {
            getEntries(data.errors).forEach(([key, values]) => {
              if (key === "base") {
                showAlert(values[0]);
                return;
              }
              values.forEach((value) => {
                methods.setError(key, { type: "required", message: value });
              });
            });
          }
          break;
        default:
          if (typeof newrelic !== "undefined") {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            newrelic.noticeError(error.message);
          }
          showAlert("送信に失敗しました。時間をおいて再度お試しください。");
      }
    }
  };

  return (
    <Presentation
      reasons={reasons}
      isSp={isSp}
      handleClick={handleClick}
      handleClose={handleClose}
      handleRadioChange={handleRadioChange}
      handleTextAreaChange={handleTextAreaChange}
      methods={methods}
      isOpen={isOpen}
      onSubmit={onSubmit}
      selectedReason={selectedReason}
    />
  );
};
